<template>
    <!-- Collection of shifts -->
    <div
        v-if="isResourceOfThisRoster"
        class="deployments-holder deployment-container"
        ref="root"

        @mouseenter="enableTooltip"
        @mouseleave="disableTooltip"
        @drop.prevent="handleDragDropEvents"
        @dragover.prevent="handleDragDropEvents"
        @dragleave.prevent="handleDragDropEvents"
    >
        <!-- Loop trough deployments -->
        <template v-if="deployments">
            <template
                v-for="(deployment, deploymentIndex) in deployments"
                :key="deploymentIndex"
            >
                <!-- Render a deployment auto complete -->
                <DeploymentMarker
                    :horizontalPosition="dayIndex"
                    :verticalPosition="resourceIndex"
                    :deploymentIndex="deploymentIndex"
                    :resource="resource"
                    :company="company"
                    :companyEmployee="companyEmployee"
                    :deployment="deployment"
                    :day="day"
                    :rosterId="rosterId"
                    :rosterNames="rosterNames"
                    :showWarning="showMarkerWarnings"
                    :shift="shifts[deployment.shiftId]"
                    :organisationDeploymentStates="organisationDeploymentStates"
                    :ref="'markers-' + dayIndex + '-' + resourceIndex"
                    :editable="editable"
                    :wish="wish"
                    :unavailabilities="unavailabilities"
                    @create:deployment="$emit('create:deployment', $event)"
                    @update:deployment="$emit('update:deployment', $event)"
                    @delete:deployment="$emit('delete:deployment', $event)"
                />
            </template>
        </template>
        <template v-if="proposalsToShow">
            <template
                v-for="(deploymentProposal, deploymentProposalIndex) in proposalsToShow"
                :key="deploymentProposalIndex"
            >
                <!-- Marker for proposal => if you can _edit_ deployments, it's acceptable -->
                <DeploymentMarker
                    :horizontalPosition="dayIndex"
                    :verticalPosition="resourceIndex"
                    :deploymentIndex="deploymentProposalIndex"
                    :resource="resource"
                    :company="company"
                    :companyEmployee="companyEmployee"
                    :deploymentProposal="deploymentProposal"
                    :day="day"
                    :rosterId="rosterId"
                    :rosterNames="rosterNames"
                    :showWarning="showMarkerWarnings"
                    :shift="shifts[deploymentProposal.shiftId]"
                    :organisationDeploymentStates="organisationDeploymentStates"
                    :ref="'markers-' + dayIndex + '-' + resourceIndex"
                    :acceptable="editable"
                    :wish="wish"
                    :unavailabilities="unavailabilities"
                    @create:deployment="$emit('create:deployment', $event)"
                    @update:deployment="$emit('update:deployment', $event)"
                    @delete:deployment="$emit('delete:deployment', $event)"
                />
            </template>
        </template>
        <DeploymentAddButton
            v-if="isEmployed && editable"
            :horizontalPosition="dayIndex"
            :verticalPosition="resourceIndex"
            :deploymentIndex="deployments ? deployments.length : 0"
        />
    </div>
    <div
        v-else
        ref="root"
        class="deployment-container"
        @mouseenter="enableTooltip"
        @mouseleave="disableTooltip"
    >
        <template v-for="(deployment, deploymentIndex) in deployments" :key="deploymentIndex">
            <DeploymentMarker
                :horizontalPosition="dayIndex"
                :verticalPosition="resourceIndex"
                :deploymentIndex="deploymentIndex"
                :resource="resource"
                :company="company"
                :companyEmployee="companyEmployee"
                :deployment="deployment"
                :day="day"
                :rosterId="rosterId"
                :rosterNames="rosterNames"
                :showWarning="showMarkerWarnings"
                :shift="shifts[deployment.shiftId]"
                :organisationDeploymentStates="organisationDeploymentStates"
                :ref="'markers-' + dayIndex + '-' + resourceIndex"
                isOtherRoster
                :editable="editable"
                :wish="wish"
                :unavailabilities="unavailabilities"
                @create:deployment="$emit('create:deployment', $event)"
                @update:deployment="$emit('update:deployment', $event)"
                @delete:deployment="$emit('delete:deployment', $event)"
            />
        </template>
        <template v-if="proposalsToShow && canProposeDeployments">
            <template
                v-for="(deploymentProposal, deploymentProposalIndex) in proposalsToShow"
                :key="deploymentProposalIndex"
            >
                <DeploymentMarker
                    :horizontalPosition="dayIndex"
                    :verticalPosition="resourceIndex"
                    :deploymentIndex="deploymentProposalIndex"
                    :resource="resource"
                    :company="company"
                    :companyEmployee="companyEmployee"
                    :deploymentProposal="deploymentProposal"
                    :day="day"
                    :rosterId="rosterId"
                    :rosterNames="rosterNames"
                    :showWarning="showMarkerWarnings"
                    :shift="shifts[deploymentProposal.shiftId]"
                    :organisationDeploymentStates="organisationDeploymentStates"
                    :ref="'markers-' + dayIndex + '-' + resourceIndex"
                    editable
                    :wish="wish"
                    :unavailabilities="unavailabilities"
                    @create:deployment="$emit('create:deployment', $event)"
                    @update:deployment="$emit('update:deployment', $event)"
                    @delete:deployment="$emit('delete:deployment', $event)"
                />
            </template>
        </template>
        <DeploymentProposeButton
            v-if="canProposeDeployments"
            :horizontalPosition="dayIndex"
            :verticalPosition="resourceIndex"
            :deploymentIndex="deployments ? deployments.length : 0"
        />
    </div>
</template>

<script lang="ts">
    import {ToolTipData, DeploymentDragData} from '@/types';
    import Tooltip from '@/components/Tooltip/Tooltip.vue';
    import DeploymentMarker from '@/components/Planbord/DeploymentMarker.vue';
    import DeploymentAddButton from '@/components/Planbord/DeploymentAddButton.vue';
    import DeploymentProposeButton from '@/components/Planbord/DeploymentProposeButton.vue';
    import Dates from '@cquential/date-utils';
    import {Deployment, OrganisationDeploymentState, Shift, PersonalPlanningWish, Unavailability, DeploymentProposal} from '@cquential/api';
    import DeploymentUtil from '@/util/deploymentutil';
    import Notify from '@/components/modals/Notify.vue';
    import {RosterDay, WrDragData} from '@/types/types';
    import {ResourceWithKeyedByDateObjects, CompanyWithDeploymentsAndEmployees, CompanyEmployeeWithDeployments} from '@/types/api-object-extensions';
    import KeyboardDeploymentAutocomplete from '@/components/deploymentAutocomplete/KeyboardDeploymentAutocomplete.vue';
    import {Component, Vue, Prop, Ref} from 'vue-facing-decorator';

    @Component({
        components: {
            DeploymentMarker,
            DeploymentAddButton,
            DeploymentProposeButton,
        },
        emits: [
            'create:deployment',
            'update:deployment',
            'delete:deployment',
        ],
    })
    export default class DeploymentCell extends Vue {
        @Prop() public company?: CompanyWithDeploymentsAndEmployees;
        @Prop() public companyEmployee?: CompanyEmployeeWithDeployments;
        @Prop({required: true}) public day!: RosterDay;
        @Prop({required: true, type: Number}) public dayIndex!: number;
        @Prop({required: true}) public deployments!: Deployment[];
        @Prop({required: true}) public deploymentProposals!: DeploymentProposal[];
        @Prop() public resource?: ResourceWithKeyedByDateObjects;
        @Prop({required: true, type: Number}) public resourceIndex!: number;
        @Prop({type: Boolean}) public isEmployed!: boolean;
        @Prop({type: Boolean}) public showMarkerWarnings!: boolean;
        @Prop({type: Boolean}) public isResourceOfThisRoster!: boolean;
        @Prop({required: true, type: Number}) public rosterId!: number;
        @Prop({required: true}) public rosterNames!: Record<number, string>;
        @Prop({required: true}) public organisationDeploymentStates!: Record<number, OrganisationDeploymentState>;
        @Prop({required: true}) public shifts!: Record<number, Shift>;
        @Prop({type: Boolean}) public editable!: boolean;
        @Prop({type: Boolean}) public canProposeDeployments!: boolean;
        @Prop() public wish?: PersonalPlanningWish;
        @Prop({required: true}) public unavailabilities!: Unavailability[];

        @Ref() public readonly root!: HTMLDivElement;

        public get proposalsToShow(): DeploymentProposal[] {
            return this.deploymentProposals.filter((proposal: DeploymentProposal): boolean => !proposal.refused && proposal.deploymentId === null);
        }

        // Drag and drop events
        public handleDragDropEvents(event: DragEvent): void {
            if (!this.root) {
                return;
            }

            switch (event.type) {
                // Receiving data (dragover, dragleave, drop)
                case 'dragover':
                    // Add class on dragover
                    this.root.classList.add('dragover');

                    if (this.dropDisabled && event.dataTransfer) {
                        event.dataTransfer.dropEffect = 'none';
                        /** @see Tooltip.closeTooltip() */
                        // @ts-expect-error TS2339
                        Tooltip.__vfdConstructor.closeTooltip();
                    } else if (this.shouldDisplayDayHighlightTooltipOnDay(event)) {
                        let tooltipData: ToolTipData;
                        if (this.resource) {
                            tooltipData = {
                                type: 'dragOver',
                                day: this.day.date,
                                event,
                                resource: this.resource,
                            };
                        } else {
                            tooltipData = {
                                type: 'dragOver',
                                day: this.day.date,
                                event,
                                company: this.company!,
                            };
                        }

                        /** @see Tooltip.tooltipIsAlreadyShownForData() */
                        // @ts-expect-error TS2339
                        if (!Tooltip.__vfdConstructor.tooltipIsAlreadyShownForData(tooltipData)) {
                            /** @see Tooltip.setTooltip() */
                            // @ts-expect-error TS2339
                            Tooltip.__vfdConstructor.setTooltip(tooltipData);
                        }
                    } else {
                        /** @see Tooltip.closeTooltip() */
                        // @ts-expect-error TS2339
                        Tooltip.__vfdConstructor.closeTooltip();
                    }
                    /** @see KeyboardDeploymentAutocomplete.disable() */
                    // @ts-expect-error TS2339
                    KeyboardDeploymentAutocomplete.__vfdConstructor.disable();
                    break;
                case 'dragleave':
                    // Remove class on dragleave
                    this.root.classList.remove('dragover');
                    break;
                case 'drop':
                    this.root.classList.remove('dragover');
                    this.dropDeployment(event);
                    break;
            }
        }

        private get dropDisabled(): boolean {
            return !!this.company?.openShiftMarketplaceSyncEnabled && !this.companyEmployee;
        }

        private shouldDisplayDayHighlightTooltipOnDay(event: DragEvent): boolean {
            if ((!this.resource || !this.resource.id) && !this.company) {
                return false;
            }

            return event.target instanceof HTMLElement && Boolean(event.dataTransfer) && event.dataTransfer!.types[0] === 'text/plain';
        }

        private dropDeployment(event: DragEvent): void {
            if ((!this.resource || !this.resource.id) && !this.company) {
                throw new Error('Either a persisted resource or the company must be defined.');
            }
            if (event.target instanceof HTMLElement && event.dataTransfer) {
                let parsedData: WrDragData | DeploymentDragData;
                try {
                    parsedData = JSON.parse(event.dataTransfer.getData('text'));
                } catch {
                    event.preventDefault();

                    return;
                }
                const draggedDeployment: Deployment = DeploymentUtil.convertDeploymentDragDataToRosterDeployment(parsedData);
                // No need to do something when dragged on same cell
                if (this.company?.openShiftMarketplaceSyncEnabled && !this.companyEmployee) {
                    /** @see Notify.createNotification() */
                    // @ts-expect-error TS2339
                    Notify.__vfdConstructor.createNotification({text: 'Een uitzendbureau die gekoppeld is aan de Open Diensten Marktplaats kan niet direct een dienst vervullen, koppel hiervoor eerst een medewerker.', type: 'info'});

                    return;
                }
                if (this.isDeploymentSameAsCurrentResourceOrCompanyOrCompanyEmployee(draggedDeployment) && Dates.compare(draggedDeployment.date).isSameDay(this.day.date)) {
                    event.preventDefault();

                    return;
                }
                if (this.shouldRemovePositionDeployments(draggedDeployment)) {
                    if (draggedDeployment.positionAssignmentSetGroupPositionDeployments) {
                        draggedDeployment.positionAssignmentSetGroupPositionDeployments = undefined;
                    }
                    /** @see Notify.createNotification() */
                    // @ts-expect-error TS2339
                    Notify.__vfdConstructor.createNotification({text: 'De gekoppelde positietoewijzing is verwijderd.', type: 'info'});
                }

                if (!draggedDeployment.id) {
                    this.createNewDeploymentFromDragDataOnThisCell(draggedDeployment);
                } else {
                    this.transferDeploymentFromDragDataToThisCell(draggedDeployment);
                }
            }
        }

        private shouldRemovePositionDeployments(draggedDeployment: Deployment): boolean {
            if (!draggedDeployment.positionAssignmentSetGroupPositionDeployments || draggedDeployment.positionAssignmentSetGroupPositionDeployments.length === 0) {
                return false;
            }

            return (this.resource?.id ?? false) !== draggedDeployment.resourceId || Dates.compare(new Date(draggedDeployment.date)).isSameDay(this.day.date);
        }

        private createNewDeploymentFromDragDataOnThisCell(draggedDeployment: Deployment): void {
            draggedDeployment.date = new Date(this.day.date);
            draggedDeployment.resourceId = this.resource?.id ?? null;
            draggedDeployment.companyId = this.company?.id ?? null;
            draggedDeployment.companyEmployeeId = this.companyEmployee?.id ?? null;
            draggedDeployment.specified = draggedDeployment.specified ?? false;
            draggedDeployment.specifications = draggedDeployment.specifications ?? DeploymentUtil.createSpecificationsFromTemplateSpecifications(
                draggedDeployment.date,
                this.shifts[draggedDeployment.shiftId]!.template!.specifications,
                draggedDeployment.stateId,
            );

            this.$emit('create:deployment', draggedDeployment);
        }

        private transferDeploymentFromDragDataToThisCell(draggedDeployment: Deployment): void {
            const deploymentWithUpdatedDate: Deployment = DeploymentUtil.cloneWithNewDate(draggedDeployment, this.day.date);

            if (this.resource && this.resource.id) {
                deploymentWithUpdatedDate.companyId = null;
                deploymentWithUpdatedDate.resourceId = this.resource.id;
            }

            if (this.company && this.company.id) {
                deploymentWithUpdatedDate.resourceId = null;
                deploymentWithUpdatedDate.companyId = this.company.id;
                deploymentWithUpdatedDate.companyEmployeeId = this.companyEmployee?.id ?? null;
            }

            this.$emit('create:deployment', deploymentWithUpdatedDate);
            this.$emit('delete:deployment', draggedDeployment);
        }

        public enableTooltip(event: Event): void {
            if (this.resource) {
                const tooltip: ToolTipData = {
                    resource: this.resource,
                    day: this.day.date,
                    event,
                    deployments: this.deployments,
                    type: 'deployment',
                };
                /** @see Tooltip.setTooltip() */
                // @ts-expect-error TS2339
                Tooltip.__vfdConstructor.setTooltip(tooltip);
            } else if (this.company) {
                const tooltip: ToolTipData = {
                    company: this.company,
                    day: this.day.date,
                    event,
                    deployments: this.deployments,
                    type: 'deployment',
                };
                /** @see Tooltip.setTooltip() */
                // @ts-expect-error TS2339
                Tooltip.__vfdConstructor.setTooltip(tooltip);
            }
        }

        public disableTooltip(): void {
            /** @see Tooltip.closeTooltip() */
            // @ts-expect-error TS2339
            Tooltip.__vfdConstructor.closeTooltip();
        }

        private isDeploymentSameAsCurrentResourceOrCompanyOrCompanyEmployee(deployment: Deployment): boolean {
            return !!(
                (deployment.resourceId && deployment.resourceId === this.resource?.id)
                || (deployment.companyId && !deployment.companyEmployeeId && deployment.companyId === this.company?.id)
                || (deployment.companyEmployeeId && deployment.companyEmployeeId === this.companyEmployee?.id)
            );
        }
    }
</script>

<style lang="scss" scoped>
    .dragover {
        .deployments-holder {
            background: #aaa;
        }
    }

    .deployment-container {
        margin-top: calc(-1 * var(--deployment-marker-spacing));
        margin-right: calc(-1 * var(--deployment-marker-spacing));

        &:only-child {
            height: 100%;
        }
    }
</style>
